import { useEffect, useRef, useState } from "react";
import { Box, Flex, Button, Text, Icon } from "@chakra-ui/react";
import { X, RefreshCw, Check, Camera } from "lucide-react";
import { colors } from "@/theme/colors";

// Camera stream lifecycle: start rear camera ONCE on mount, stop tracks on
// unmount, Escape closes. onClose is kept in a ref so parent re-renders
// (draft autosave etc.) can never restart or kill the live stream.
const useCameraStream = (videoRef, onClose) => {
  const streamRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch {
        if (!cancelled) closeRef.current("fallback");
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeRef.current(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return ready;
};

// In-app camera (getUserMedia): the page never goes to background, so the
// capture can't be lost to an Android tab restart. Calls onClose("fallback")
// if the camera can't start, letting the caller use a native file input.
// The <video> stays mounted during preview — unmounting it drops srcObject,
// which made Retake open onto a black screen.
export const CameraCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const [shot, setShot] = useState(null);
  const ready = useCameraStream(videoRef, onClose);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const maxW = 1600;
    const scale = Math.min(1, maxW / v.videoWidth);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(v.videoWidth * scale);
    canvas.height = Math.round(v.videoHeight * scale);
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    setShot(canvas.toDataURL("image/jpeg", 0.85));
  };

  return (
    <Flex position="fixed" inset="0" zIndex={2147483200} bg="black" direction="column" data-testid="camera-modal"
      role="dialog" aria-modal="true" aria-label={shot ? "Photo preview" : "Take photo"}>
      <Flex justify="space-between" align="center" px="4" py="3">
        <Text color="white" fontWeight="600" fontSize="sm">{shot ? "Use this photo?" : "Take photo"}</Text>
        <Button size="xs" variant="ghost" onClick={() => onClose()} data-testid="camera-close-btn"><X size={18} /></Button>
      </Flex>
      <Box flex="1" position="relative" overflow="hidden">
        <Box as="video" ref={videoRef} playsInline muted autoPlay w="full" h="full" objectFit="cover"
          visibility={shot ? "hidden" : "visible"} data-testid="camera-video" />
        {shot && (
          <Box as="img" src={shot} alt="Captured" position="absolute" inset="0" w="full" h="full"
            objectFit="contain" bg="black" data-testid="camera-shot-preview" />
        )}
        {!ready && !shot && (
          <Flex position="absolute" inset="0" align="center" justify="center">
            <Text color="whiteAlpha.700" fontSize="sm">Starting camera…</Text>
          </Flex>
        )}
      </Box>
      <Flex justify="center" align="center" gap="6" py="5" pb="8">
        {shot ? (
          <>
            <Button variant="outline" borderColor="whiteAlpha.500" px="5"
              onClick={() => setShot(null)} data-testid="camera-retake-btn">
              <RefreshCw size={16} /> Retake
            </Button>
            <Button px="6"
              onClick={() => onCapture(shot)} data-testid="camera-use-btn">
              <Check size={16} /> Use photo
            </Button>
          </>
        ) : (
          <Box as="button" type="button" onClick={capture} disabled={!ready} data-testid="camera-shutter-btn"
            w="68px" h="68px" borderRadius="full" bg="white" border="5px solid" borderColor="whiteAlpha.600"
            display="flex" alignItems="center" justifyContent="center" opacity={ready ? 1 : 0.5}>
            <Icon as={Camera} boxSize="6" color={colors.textPrimary} />
          </Box>
        )}
      </Flex>
    </Flex>
  );
};
